import type {
  PlayOutboxStorage,
  StoredEvent,
  StoredPlay,
} from "./createPlayOutboxStorage";

// The app's SQLite web adapter is a no-op. Keep the same synchronous outbox
// contract using browser persistence so pagehide/reloads cannot erase evidence.
const KEY = "lissen-play-outbox-v2";
type State = { events: StoredEvent[]; plays: Record<string, StoredPlay> };
export function createPlayOutboxStorage(): PlayOutboxStorage {
  let memory: State = { events: [], plays: {} };
  let memoryOnly = false;
  function read(): State {
    if (memoryOnly) return memory;
    try {
      return (
        (JSON.parse(localStorage.getItem(KEY) || "null") as State) || memory
      );
    } catch {
      return memory;
    }
  }
  function write(state: State) {
    state.events = [...state.events]
      .sort((a, b) => a.created_at - b.created_at)
      .slice(-5000);
    memory = state;
    try {
      localStorage.setItem(KEY, JSON.stringify(state));
      memoryOnly = false;
    } catch {
      // Prefer the latest in-memory state over an older persisted snapshot.
      memoryOnly = true;
    }
  }
  // A play is another account's when it has an owner who is not `owner`.
  const visibleTo =
    (state: State, owner: string | null) => (e: StoredEvent) => {
      const playOwner = state.plays[e.play_id]?.user_id ?? null;
      return playOwner === null || playOwner === owner;
    };
  return {
    enqueue(event, owner) {
      const state = read();
      if (!state.events.some((e) => e.id === event.id))
        state.events.push(event);
      if (event.kind === "start")
        state.plays[event.play_id] ??= {
          play_id: event.play_id,
          user_id: owner,
          token: null,
          last_seq: 0,
          interval_seconds: 30,
          song_id: event.song_id!,
          started_at: event.created_at,
          last_event_at: event.created_at,
        };
      if (state.plays[event.play_id])
        state.plays[event.play_id].last_event_at = event.created_at;
      write(state);
    },
    select: (owner) => {
      const state = read();
      return state.events
        .filter(visibleTo(state, owner))
        .sort((a, b) => a.play_id.localeCompare(b.play_id) || a.seq - b.seq)
        .slice(0, 1000)
        .map((event) => ({ ...event }));
    },
    getPlay: (id) => read().plays[id],
    acknowledge(ids, credentials) {
      const state = read();
      const removed = new Set(ids);
      state.events = state.events.filter((e) => !removed.has(e.id));
      for (const [id, value] of Object.entries(credentials))
        if (state.plays[id]) Object.assign(state.plays[id], value);
      write(state);
    },
    prune(before) {
      const state = read();
      state.events = state.events.filter(
        (e) => e.created_at >= Date.now() - 31 * 86400000,
      );
      const pending = new Set(state.events.map((e) => e.play_id));
      for (const [id, play] of Object.entries(state.plays))
        if (play.last_event_at < before && !pending.has(id))
          delete state.plays[id];
      state.events = state.events.filter((e) =>
        Boolean(state.plays[e.play_id]),
      );
      write(state);
    },
    forget(owner) {
      const state = read();
      const forgotten = new Set(
        Object.values(state.plays)
          .filter((play) => play.user_id === owner)
          .map((play) => play.play_id),
      );
      state.events = state.events.filter((e) => !forgotten.has(e.play_id));
      for (const id of forgotten) delete state.plays[id];
      write(state);
      // Deletion needs no quota: when writes fail, do not leave the account's
      // plays on disk for the next page load. Memory keeps everyone else's.
      if (memoryOnly)
        try {
          localStorage.removeItem(KEY);
        } catch {
          // Storage may be unavailable altogether in private browsing.
        }
    },
    size: (owner) => {
      const state = read();
      return state.events.filter(visibleTo(state, owner)).length;
    },
  };
}
